<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Agregar Orden de Compra"
                        aria-label="Agregar Orden de Compra"
                        @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Proveedores</label>
              <v-select :options="arr.filters.suppliers"
                        v-model="filters.suppliers"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"
                        :get-option-key="getFilterOptionKey"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;">
              <label class="filter-label">Estados</label>
              <v-select :options="arr.filters.status"
                        v-model="filters.status"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"
                        :get-option-key="getFilterOptionKey"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 150px; max-width: 190px;">
              <label class="filter-label">ID de orden</label>
              <b-form-input
                type="search"
                placeholder="Buscar ID"
                v-model="filters.id"
                size="sm"
                @input="onInputIdFilter"
                @keyup.enter="filterPurchaseOrders(true)">
              </b-form-input>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterPurchaseOrders(true)" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12" id="view_list_purchase_orders">
          <b-card no-body>
            <b-row no-gutters>
              <b-col sm="12">
                <b-table class="app-table"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        @row-clicked="onRowClicked"
                        :tbody-tr-class="rowClass"
                        :busy="table.isBusy"
                        v-if="table.items.length || table.isBusy">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="(field, index) in scope.fields"
                      :key="(field.key || 'column') + '_' + index"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong>Cargando...</strong>
                    </div>
                  </template>

                  <template v-slot:cell(voucher)="data">
                    <span>ORDEN DE COMPRA #{{data.item.id}}</span>
                  </template>

                  <template v-slot:cell(date)="data">
                    {{moment(data.item.date).format('DD/MM/YYYY')}}
                  </template>

                  <template v-slot:cell(suppliers_id)="data">
                    <div v-if="data.item.supplier">
                      <b-avatar :src="data.item.supplier.image"
                                v-if="data.item.supplier.image"
                                size="25px">
                      </b-avatar>
                      <b-avatar v-else
                                :text="avatarFromName(data.item.supplier.name).text"
                                :style="avatarFromName(data.item.supplier.name).style"
                                size="25px">
                      </b-avatar>
                      &nbsp;<b>{{data.item.supplier.name}}</b>
                    </div>
                  </template>

                  <template v-slot:cell(status)="data">
                    <b-badge :variant="getStatusVariant(data.item.status)">
                      {{data.item.status_label || getStatusLabel(data.item.status)}}
                    </b-badge>
                  </template>

                  <template v-slot:cell(quantity_pending)="data">
                    <span class="text-danger" v-if="parseFloat(data.item.quantity_pending)>0">
                      {{parseFloat(data.item.quantity_pending).toFixed(2)}}
                    </span>
                    <span v-else>
                      0.00
                    </span>
                  </template>

                  <template v-slot:cell(amount_total)="data">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(data.item.amount_total || 0)}}
                  </template>

                  <template v-slot:cell(f_action)="data">
                    <b-dropdown right
                                no-caret
                                size="sm"
                                variant="link"
                                boundary="window"
                                :toggle-class="isMobileViewport ? 'purchase-orders-action-toggle-labeled' : 'app-table-action-toggle'"
                                :class="{ 'pull-right': !isMobileViewport }">
                      <template #button-content>
                        <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                        <span v-if="isMobileViewport">Opciones</span>
                      </template>

                      <b-dropdown-item @click="sendPrinter(data.item)">
                        <AppIcon name="print" /> Imprimir
                      </b-dropdown-item>

                      <b-dropdown-header v-if="canShowStatusHeader(data.item)">Estados</b-dropdown-header>
                      <b-dropdown-item @click="updatePurchaseOrderStatus(data.item, 'cancelled')"
                                       v-if="canCancelPurchaseOrder(data.item)"
                                       class="text-danger">
                        <AppIcon name="cancel" class="text-danger" /> Cancelar
                      </b-dropdown-item>
                      <b-dropdown-item @click="updatePurchaseOrderStatus(data.item, 'pending')"
                                       v-if="canSetPendingPurchaseOrder(data.item)">
                        <AppIcon name="undo" class="text-warning" /> Pendiente
                      </b-dropdown-item>

                      <b-dropdown-header v-if="canShowActionsHeader(data.item)">Acciones</b-dropdown-header>
                      <b-dropdown-item @click="edit(data.item)" v-if="canEditPurchaseOrder(data.item)">
                        <AppIcon name="edit" /> Editar
                      </b-dropdown-item>
                      <b-dropdown-item @click="remove(data.item)" v-if="canDeletePurchaseOrder(data.item)">
                        <AppIcon name="delete" class="text-danger" /> Eliminar
                      </b-dropdown-item>
                    </b-dropdown>
                  </template>
                </b-table>
                <EmptyState v-else icon="cart" title="No se encontraron órdenes de compra" description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda, o generá una orden nueva con el botón Agregar." />

                <div class="app-table-footer purchase-orders-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <b-sidebar
          id="sidebar-purchase-order-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de Orden de Compra"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">ORDEN DE COMPRA #{{itemSelected.id}}</span>
              <div v-if="itemSelected.supplier">{{itemSelected.supplier.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevPurchaseOrder"
                        v-b-tooltip.hover
                        title="Orden anterior"
                        @click="goPrevPurchaseOrder()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextPurchaseOrder"
                        v-b-tooltip.hover
                        title="Orden siguiente"
                        @click="goNextPurchaseOrder()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Total estimado</span>
              <span class="list-view-drawer__stat-value">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(itemSelected.amount_total || 0)}}
              </span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': parseFloat(itemSelected.quantity_pending || 0) > 0 }">
              <span class="list-view-drawer__stat-label">Pendiente</span>
              <span class="list-view-drawer__stat-value">
                {{parseFloat(itemSelected.quantity_pending || 0).toFixed(2)}}
              </span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="list-ul" /> Detalle
            </div>

            <b-table class="app-table app-table--compact"
                    responsive
                    stacked
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableSub.items"
                    :fields="tableSub.fields"
                    v-if="tableSub.items.length">

              <template v-slot:cell(quantity)="row">
                {{parseFloat(row.item.quantity || 0).toFixed(2)}}
                <span v-if="row.item.unit_measurement">{{row.item.unit_measurement.reference}}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="getProductName(row.item)">
                  {{getProductName(row.item)}}
                </div>
              </template>
            </b-table>
            <EmptyState v-else icon="list-ul" title="Sin items cargados" />
          </div>

          <b-button v-if="tableSub.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinter(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="edit(itemSelected)" v-if="canEditPurchaseOrder(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="updatePurchaseOrderStatus(itemSelected, 'cancelled')" v-if="canCancelPurchaseOrder(itemSelected)">
              <AppIcon name="cancel" class="mr-1" /> Cancelar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && canDeletePurchaseOrder(itemSelected)">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12" id="view_footer_purchase_orders">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterPurchaseOrders()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <Printer v-if="crud.print.id"
              :reference="crud.print.reference"
              :registerID="crud.print.id"
              :key="crud.print.key"/>

      <PurchaseOrderFormModal ref="purchaseOrderForm"
                              @saved="onPurchaseOrderSaved" />

      <b-modal v-model="modal.detail.active"
              size="xl"
              :key="modal.detail._id"
              :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title>
                  <AppIcon name="description" class="mr-1" /> General
                </template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b>#{{itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value"><b>ORDEN DE COMPRA #{{itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.staff.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.date_expected">
                    <span class="app-modal-detail-list__label">Entrega esperada</span>
                    <span class="app-modal-detail-list__value">{{moment(itemSelected.date_expected).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.supplier">
                    <span class="app-modal-detail-list__label">Proveedor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.supplier.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Estado</span>
                    <span class="app-modal-detail-list__value">
                      <b-badge :variant="getStatusVariant(itemSelected.status)">
                        {{itemSelected.status_label || getStatusLabel(itemSelected.status)}}
                      </b-badge>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Remitos vinculados</span>
                    <span class="app-modal-detail-list__value">{{getDetailLinkedRemits.length}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. total</span>
                    <span class="app-modal-detail-list__value">{{parseFloat(itemSelected.quantity_total || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. entregada</span>
                    <span class="app-modal-detail-list__value">{{parseFloat(itemSelected.quantity_delivered || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. pendiente</span>
                    <span class="app-modal-detail-list__value app-modal-detail-list__value--warning">{{parseFloat(itemSelected.quantity_pending || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Total estimado</span>
                    <span class="app-modal-detail-list__value"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(itemSelected.amount_total || 0)}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.observations}}</span>
                  </div>
                </div>
              </b-tab>

              <b-tab>
                <template #title>
                  <AppIcon name="list-ul" class="mr-1" /> Items
                </template>
                <b-table class="app-table app-table--compact table-full-detail-purchase-orders"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetail.items"
                        :fields="tableDetailModal.fields"
                        v-if="tableDetail.items.length">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="(field, index) in scope.fields"
                      :key="(field.key || 'column') + '_' + index"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:cell(code)="data">
                    {{data.item.products_code || getProductCode(data.item)}}
                  </template>

                  <template v-slot:cell(product)="data">
                    <div class="purchase-orders-detail-item">
                      {{getProductName(data.item)}}
                    </div>
                  </template>

                  <template v-slot:cell(quantity)="data">
                    {{parseFloat(data.item.quantity || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                  </template>

                  <template v-slot:cell(quantity_delivered)="data">
                    {{parseFloat(data.item.quantity_delivered || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                  </template>

                  <template v-slot:cell(quantity_pending)="data">
                    <span :class="data.item.hidden_suggestion ? 'text-muted' : 'text-danger'" v-if="parseFloat(data.item.quantity_pending || 0) > 0">
                      {{parseFloat(data.item.quantity_pending || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                    </span>
                  </template>

                  <template v-slot:cell(price_cost)="data">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(data.item.price_cost || 0)}}
                  </template>

                  <template v-slot:cell(f_action)="data">
                    <b-link v-if="data.item.id && parseFloat(data.item.quantity_pending || 0) > 0"
                            class="purchase-orders-detail-action-link"
                            :title="data.item.hidden_suggestion ? 'Mostrar en sugerencias' : 'Ocultar de sugerencias'"
                            @click="toggleHiddenSuggestion(data.item)">
                      <AppIcon :name="data.item.hidden_suggestion ? 'visibility' : 'visibility_off'" class="mr-1" />
                      <span v-if="isMobileViewport">{{data.item.hidden_suggestion ? 'Mostrar' : 'Ocultar'}}</span>
                    </b-link>
                  </template>
                </b-table>
                <EmptyState v-else icon="list-ul" title="No se encontraron items" />
              </b-tab>

              <b-tab v-if="getDetailLinkedRemits.length">
                <template #title>
                  <AppIcon name="paperclip" class="mr-1" /> Remitos
                </template>
                <b-table class="app-table app-table--compact table-full-detail-purchase-orders"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="getDetailLinkedRemits"
                        :fields="tableLinkedRemits.fields">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="(field, index) in scope.fields"
                      :key="(field.key || 'column') + '_' + index"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:cell(date)="data">
                    {{moment(data.item.date).format('DD/MM/YYYY')}}
                  </template>

                  <template v-slot:cell(reference)="data">
                    {{data.item.reference}}
                  </template>

                  <template v-slot:cell(items)="data">
                    {{parseInt(data.item.items || 0)}}
                  </template>

                  <template v-slot:cell(quantity)="data">
                    {{parseFloat(data.item.quantity || 0).toFixed(2)}}
                  </template>
                </b-table>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false">
            <AppIcon name="x" class="mr-1" />Cerrar
          </b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Storage from '@/handler/storageSession'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Printer from '@/components/inc/printer/printer'
  import moment from 'moment'
  import Function from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import PurchaseOrderFormModal from './inc/PurchaseOrderFormModal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      Printer,
      PurchaseOrderFormModal,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.ORDENES_COMPRAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'purchaseOrders',
          elements: {
            deletePurchaseOrders: true,
          }
        },
        table: {
          items: [],
          fields: [],
          mostrarFiltros: true,
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,
          rowSelected: null,
        },
        tableSub: {
          items: [],
          fields: [],
        },
        tableDetail: {
          items: [],
          fields: [],
        },
        tableDetailModal: {
          fields: [],
        },
        tableLinkedRemits: {
          fields: [],
        },
        crud: {
          print: {
            id: 0,
            reference: '',
            key: Math.random(),
          },
        },
        modal: {
          detail: {
            active: false,
            title: '',
            _id: '',
          },
        },
        filters: {
          date_start: '',
          date_end: '',
          suppliers: null,
          status: null,
          id: '',
        },
        arr: {
          filters: {
            suppliers: [],
            status: [
              { code: 'pending', label: 'Pendiente' },
              { code: 'partial', label: 'Entrega parcial' },
              { code: 'delivered', label: 'Entregado' },
              { code: 'cancelled', label: 'Cancelado' },
            ],
          },
        },
        itemSelected: null,
        filterIdTimer: null,
        detailDrawer: false,
        filtersOpen: false,
        isMobileViewport: false,
      }
    },
    created () {
      Helper.hasAccessView(this.access)
      this.access.elements.deletePurchaseOrders = Helper.hasAccessElement(this.access, 'delete_purchase_orders')
    },
    computed: {
      getDetailLinkedRemits() {
        return this.buildLinkedRemits(this.itemSelected)
      },
      currentPurchaseOrderIndex() {
        if(!this.itemSelected) return -1
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevPurchaseOrder() {
        return this.currentPurchaseOrderIndex > 0
      },
      canGoNextPurchaseOrder() {
        return this.currentPurchaseOrderIndex >= 0 && this.currentPurchaseOrderIndex < this.table.items.length - 1
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_purchase_orders') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldsTable()
      this.filterLoad()
      this.filterPurchaseOrders()

      // Acción directa desde la paleta de comandos: abrir el alta al entrar.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterPurchaseOrders()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos no abriría nada sin este chequeo acá también.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    beforeDestroy() {
      if(this.filterIdTimer) {
        clearTimeout(this.filterIdTimer)
      }
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_purchase_orders', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldsTable()
      },
    },
    methods: {
      moment,

      loadFieldsTable() {
        this.table.fields = []
        this.tableSub.fields = []
        this.tableDetailModal.fields = []
        this.tableLinkedRemits.fields = []

        this.table.fields.push({key: 'voucher', label: 'Comprobante', class:'align-middle text-left', width:'240px'})
        this.table.fields.push({key: 'date', label: 'Fecha', class:'align-middle ' + this.alignClass('text-center'), width:'90px'})
        this.table.fields.push({key: 'suppliers_id', label: 'Proveedor', class:'align-middle', width:'180px'})
        this.table.fields.push({key: 'status', label: 'Estado', class:'align-middle ' + this.alignClass('text-center'), width:'110px'})
        this.table.fields.push({key: 'quantity_pending', label: 'Pendiente', class:'align-middle ' + this.alignClass('text-right'), width:'90px'})
        this.table.fields.push({key: 'amount_total', label: 'Total', class:'align-middle ' + this.alignClass('text-right'), width:'100px'})
        this.table.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:'align-middle ' + this.alignClass('text-right'), width:'70px'})

        this.tableSub.fields.push({key: 'quantity', label: 'Cantidad', class:'align-middle', width:'70px'})
        this.tableSub.fields.push({key: 'product', label: 'Item', class:'align-middle', width:'230px'})

        this.tableDetailModal.fields.push({key: 'code', label: 'Código', class:'align-middle text-left', width:'90px'})
        this.tableDetailModal.fields.push({key: 'product', label: 'Item', class:'align-middle text-left', width:'240px'})
        this.tableDetailModal.fields.push({key: 'quantity', label: 'Cant.', class:'align-middle ' + this.alignClass('text-right'), width:'70px'})
        this.tableDetailModal.fields.push({key: 'quantity_delivered', label: 'Entregado', class:'align-middle ' + this.alignClass('text-right'), width:'80px'})
        this.tableDetailModal.fields.push({key: 'quantity_pending', label: 'Pendiente', class:'align-middle ' + this.alignClass('text-right'), width:'80px'})
        this.tableDetailModal.fields.push({key: 'price_cost', label: 'Costo', class:'align-middle ' + this.alignClass('text-right'), width:'105px'})
        this.tableDetailModal.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:'align-middle text-left', width:'78px'})

        this.tableLinkedRemits.fields.push({key: 'date', label: 'Fecha', class:'align-middle text-center', width:'110px'})
        this.tableLinkedRemits.fields.push({key: 'reference', label: 'Comprobante', class:'align-middle text-left'})
        this.tableLinkedRemits.fields.push({key: 'supplier', label: 'Proveedor', class:'align-middle text-left'})
        this.tableLinkedRemits.fields.push({key: 'items', label: 'Items', class:'align-middle text-right', width:'90px'})
        this.tableLinkedRemits.fields.push({key: 'quantity', label: 'Cant. entregada', class:'align-middle text-right', width:'130px'})
      },
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Function.avatarFromName(name)
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: options.size || 'sm',
          buttonSize: options.buttonSize || 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },

      // FILTERS
      filterLoad() {
        this.filterLoadSuppliers()

        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
        this.filters.id = ''

        this.restoreFilterStorage()
      },
      onInputIdFilter() {
        var onlyNumbers = (this.filters.id || '').toString().replace(/[^0-9]/g, '')
        if(onlyNumbers !== this.filters.id) {
          this.filters.id = onlyNumbers
        }

        if(this.filterIdTimer) {
          clearTimeout(this.filterIdTimer)
        }

        this.filterIdTimer = setTimeout(() => {
          this.filterPurchaseOrders(true)
        }, 350)
      },
      getFilterOptionKey(option) {
        if(option && option.code !== undefined && option.code !== null && option.code !== '') {
          return String(option.code)
        }

        if(option && option.label) {
          return String(option.label)
        }

        return 'option-empty'
      },
      filterLoadSuppliers() {
        serviceAPI.obtenerProveedores()
        .then((response) => {
          var data = response.data
          this.arr.filters.suppliers = []
          var added = {}

          data.forEach((element) => {
            if(!element || !element.id) {
              return
            }

            if(added[element.id]) {
              return
            }
            added[element.id] = true

            var label = ''
            if(element.name) {
              label = element.name.toString().trim()
            }

            this.arr.filters.suppliers.push({
              code: element.id,
              label: label ? label : ('Proveedor #' + element.id),
            })
          })
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      filterPurchaseOrders(resetPage = false) {
        if(resetPage) {
          this.table.currentPage = 1
        }

        this.saveFilterStorage()
        this.table.isBusy = true

        serviceAPI.filtrarOrdenes(this.filters, this.table.currentPage)
        .then((response) => {
          var data = response.data
          this.table.tablePaginate = data
          this.table.items = data.data

          this.table.isBusy = false

          if(this.itemSelected) {
            this.table.items.forEach((element) => {
              if(element.id === this.itemSelected.id) {
                this.openSub(element)
              }
            })
          }
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(ErrorToken.valid(error))
        })
      },
      saveFilterStorage() {
        Storage.setValue('filter_crud_purchase_orders', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_crud_purchase_orders')) {
          var data = JSON.parse(Storage.getValue('filter_crud_purchase_orders'))
          this.filters = Object.assign({}, this.filters, data)
        }

        if(this.filters.id === null || this.filters.id === undefined) {
          this.filters.id = ''
        } else {
          this.filters.id = this.filters.id.toString().replace(/[^0-9]/g, '')
        }
      },

      // TABLE / DETAIL
      onRowSelected(item) {
        if(item.length) {
          this.openSub(item[0])
        }
      },
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      goPrevPurchaseOrder() {
        if(!this.canGoPrevPurchaseOrder) return
        this.openSub(this.table.items[this.currentPurchaseOrderIndex - 1])
      },
      goNextPurchaseOrder() {
        if(!this.canGoNextPurchaseOrder) return
        this.openSub(this.table.items[this.currentPurchaseOrderIndex + 1])
      },
      openSub(item) {
        this.itemSelected = item
        this.tableSub.items = item.details || []
        this.detailDrawer = true
      },
      hideSub() {
        this.itemSelected = null
        this.tableSub.items = []
        this.detailDrawer = false
      },
      openDetail(item = null) {
        var selected = item || this.itemSelected
        if(!selected) {
          return false
        }

        this.itemSelected = selected
        this.tableSub.items = this.itemSelected.details || []
        this.tableDetail.items = this.itemSelected.details || []

        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = 'Detalle de Orden de Compra'
        this.modal.detail.active = true

        let loader = this.$loading.show()
        serviceAPI.mostrarOrden(this.itemSelected.id)
        .then((response) => {
          var data = response.data
          this.itemSelected = data
          this.tableSub.items = data.details || []
          this.tableDetail.items = data.details || []
          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      canCancelPurchaseOrder(item) {
        return item && item.status === 'pending'
      },
      canSetPendingPurchaseOrder(item) {
        return item && item.status === 'cancelled'
      },
      canShowStatusHeader(item) {
        return this.canCancelPurchaseOrder(item) || this.canSetPendingPurchaseOrder(item)
      },
      canEditPurchaseOrder(item) {
        return item && item.status === 'pending'
      },
      canDeletePurchaseOrder(item) {
        return item && this.access.elements.deletePurchaseOrders && item.status === 'pending'
      },
      canShowActionsHeader(item) {
        return this.canEditPurchaseOrder(item) || this.canDeletePurchaseOrder(item)
      },
      updatePurchaseOrderStatus(item, status) {
        if(!item || !item.id) {
          return false
        }

        var toLabel = status === 'cancelled' ? 'Cancelado' : 'Pendiente'
        var actionLabel = status === 'cancelled' ? 'cancelar' : 'pasar a pendiente'

        this.confirmDialog('¿Desea ' + actionLabel + ' la orden de compra seleccionada?', {
          title: 'Cambiar Estado',
          icon: status === 'cancelled' ? 'cancel' : 'undo',
          danger: status === 'cancelled',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then((value) => {
          if(!value) {
            return
          }

          let loader = this.$loading.show()
          serviceAPI.actualizarEstadoOrden(item.id, { status: status })
          .then((response) => {
            loader.hide()
            var data = response.data

            if(this.itemSelected && parseInt(this.itemSelected.id) === parseInt(item.id)) {
              this.openSub(data)
            }

            this.filterPurchaseOrders()
            this.$awn.success('Estado actualizado a ' + toLabel)
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
        })
      },

      // CRUD MAIN
      add() {
        this.$refs.purchaseOrderForm.add()
      },
      edit(item) {
        this.$refs.purchaseOrderForm.edit(item)
      },
      remove(item) {
        if(item.status !== 'pending') {
          this.$awn.alert('Solo se pueden eliminar órdenes pendientes')
          return false
        }

        this.confirmDialog('¿Desea borrar la orden de compra seleccionada?', {
          title: 'Borrar Orden de Compra',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.eliminarOrden(item.id)
            .then(() => {
              loader.hide()
              this.hideSub()
              this.filterPurchaseOrders()
              this.$awn.success('Orden eliminada')
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      onPurchaseOrderSaved(data) {
        this.filterPurchaseOrders()

        if(data && data.id) {
          this.openSub(data)
        }
      },

      // FLOW SUPPLIER

      // SUGGESTIONS
      sendPrinter(item) {
        if(!item || !item.id) {
          this.$awn.alert('No se encontró la orden de compra para imprimir')
          return false
        }

        this.crud.print.key = Math.random()
        this.crud.print.id = item.id
        this.crud.print.reference = item.reportDefault || 'purchase-order'
      },

      // HELPERS
      replaceItemInList(data) {
        if(!data || !data.id) {
          this.filterPurchaseOrders()
          return false
        }

        var index = this.table.items.findIndex((element) => element.id === data.id)
        if(index >= 0) {
          this.$set(this.table.items, index, data)
        } else {
          this.filterPurchaseOrders()
        }
      },
      getProductName(item) {
        if(item.products_name) {
          return item.products_name
        }

        if(item.product && item.product.name) {
          return item.product.name
        }

        if(item.select_item && item.select_item.name) {
          return item.select_item.name
        }

        if(item.select_product && item.select_product.name) {
          return item.select_product.name
        }

        return ''
      },
      getProductCode(item) {
        if(item.products_code) {
          return item.products_code
        }

        if(item.product && item.product.code) {
          return item.product.code
        }

        if(item.select_item && item.select_item.code) {
          return item.select_item.code
        }

        if(item.select_product && item.select_product.code) {
          return item.select_product.code
        }

        return ''
      },
      getUnitMeasurement(item) {
        var unitMeasurement = item && item.unit_measurement ? item.unit_measurement : null

        if(!unitMeasurement && item && item.product) {
          unitMeasurement = item.product.unit_measurement_purchase_default || item.product.unit_measurement_purchase || null
        }

        if(!unitMeasurement) {
          return { reference: 'UN', name: 'Unidad' }
        }

        return unitMeasurement
      },
      getUnitMeasurementReference(item) {
        var unitMeasurement = this.getUnitMeasurement(item)
        return unitMeasurement.reference || 'UN'
      },
      getRemitReference(remit) {
        if(!remit) {
          return ''
        }

        var typeVoucher = remit.type_voucher || remit.typeVoucher
        var name = typeVoucher && typeVoucher.name ? typeVoucher.name + ' ' : 'Remito '
        var pointSale = parseInt(remit.point_sale || 0).toString().padStart(4, '0')
        var number = parseInt(remit.number || 0).toString().padStart(8, '0')
        return name + pointSale + '-' + number
      },
      buildLinkedRemits(order) {
        if(!order || !order.details || !order.details.length) {
          return []
        }

        var remits = {}

        order.details.forEach((detail) => {
          var links = detail.purchase_remits_detail || detail.purchaseRemitsDetail || detail.PurchaseRemitsDetail || []
          if(!Array.isArray(links)) {
            return
          }

          links.forEach((link) => {
            var remit = link.remit || link.Remit
            if(!remit || !remit.id) {
              return
            }

            if(!remits[remit.id]) {
              remits[remit.id] = {
                id: remit.id,
                date: remit.date,
                reference: this.getRemitReference(remit),
                supplier: order.supplier ? order.supplier.name : '',
                items: 0,
                quantity: 0,
              }
            }

            remits[remit.id].items = remits[remit.id].items + 1
            remits[remit.id].quantity = remits[remit.id].quantity + parseFloat(link.quantity || 0)
          })
        })

        return Object.values(remits).sort((a, b) => {
          return (b.id || 0) - (a.id || 0)
        })
      },
      getStatusVariant(status) {
        if(status === 'pending') return 'warning'
        if(status === 'partial') return 'info'
        if(status === 'delivered') return 'success'
        if(status === 'cancelled') return 'danger'
        return 'secondary'
      },
      getStatusLabel(status) {
        if(status === 'pending') return 'Pendiente'
        if(status === 'partial') return 'Entrega parcial'
        if(status === 'delivered') return 'Entregado'
        if(status === 'cancelled') return 'Cancelado'
        return status
      },
      toggleHiddenSuggestion(item) {
        if(!item || !item.id) {
          return
        }

        serviceAPI.toggleHiddenSuggestion(item.id)
        .then((response) => {
          var data = response.data && response.data.data ? response.data.data : response.data
          item.hidden_suggestion = !!data.hidden_suggestion

          var label = item.hidden_suggestion ? 'ocultado' : 'visible'
          this.$awn.success('Item ' + label + ' en sugerencias')
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
    }
  }
</script>

<style>
  .table-full-detail-purchase-orders {
    max-height: 350px;
    overflow: auto;
  }
  .purchase-orders-detail-item {
    white-space: normal;
    line-height: 1.2;
  }
  .purchase-orders-table-footer {
    padding-right: 12px;
  }
  .btn.purchase-orders-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.purchase-orders-action-toggle-labeled:hover,
  .btn.purchase-orders-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  .purchase-orders-detail-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 34px;
    min-height: 28px;
    padding: 3px 7px;
    margin-left: 10px;
    border-radius: 6px;
    color: var(--app-color-text-secondary);
    font-size: 16px;
    line-height: 1.15;
  }
  .purchase-orders-detail-action-link:hover,
  .purchase-orders-detail-action-link:focus {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    text-decoration: none;
  }
  @media (max-width: 767.98px) {
    .table-full-detail-purchase-orders.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
    .purchase-orders-detail-action-link {
      min-height: 0;
      padding: 0;
      margin-left: 0;
      background: transparent;
      color: var(--app-color-text-secondary);
      font-size: 13px;
    }
    .purchase-orders-detail-action-link:hover,
    .purchase-orders-detail-action-link:focus {
      background: transparent;
      color: var(--app-color-primary);
      text-decoration: underline;
    }
  }
</style>
